import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CardActions from '@mui/material/CardActions';
import Divider from '@mui/material/Divider';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import PropTypes from 'prop-types';
import React, {useEffect, useState} from 'react';
import isEqual from 'lodash/isEqual';
import xor from 'lodash/xor';
import {connect} from 'react-redux';
import {useTranslation} from 'react-i18next';

import FilterGroupPanel from '@explorer/components/ui/filter-group-panel';
import InstrumentFilter from './instrument-filter.jsx';
import LeftDrawerHeader from '@explorer/components/sidebars/left-drawer-header';
import PublishingStageFilter from './publishing-stage-filter.jsx';
import SatelliteIdFilter from './satellite-id-filter.jsx';
import SliderFilter from './slider-filter.jsx';
import SourcesPicker from './sources-picker.jsx';
import SpectralBandsFilter from './spectral-bands-filter.jsx';
import ToggleFilter from './toggle-filter.jsx';
import {LABS_MAP} from '@explorer/constants';
import {
  applyFilters,
  resetFilters as resetItemFilters,
} from '@explorer/actions/item-filters';
import {initialState as defaultAreaCoverage} from '@explorer/reducers/area-coverage';
import {initialState as defaultItemTypes} from '@explorer/reducers/item-types';
import {initialState as defaultSearchFilters} from '@explorer/reducers/search-filters';
import {
  getAvailableItemTypes,
  getSelectedItemTypes,
} from '@explorer/selectors/item-types';
import {getFiltersObj} from '@explorer/selectors/filters';
import {
  getSelectedImageryCapabilities,
  getSr,
} from '@explorer/selectors/search-filters';
import {hasChangedFromDefaults} from '@explorer/util/filters';
import {
  resetSearchFilters,
  selectImageryCapabilities,
  setAccessPermissions,
  setInstruments,
  setPublishingStages,
  setSR,
  setSatelliteId,
  setSearchFilters,
} from '@explorer/actions/search-filters';
import {
  resetSelectedItemTypes,
  selectItemTypes,
} from '@explorer/actions/item-types';
import {setAreaCoverage} from '@explorer/actions/area-coverage';
import {showFiltersPanel} from '@explorer/actions/sidebar';
import {trackEvent} from '@explorer/analytics';

const FiltersPanel = props => {
  const {t} = useTranslation();

  const [newImageryCapabilities, setNewImageryCapabilities] = useState(
    props.selectedImageryCapabilities
  );

  const [selectedItemTypes, setSelectedItemTypes] = useState(
    props.selectedItemTypes
  );

  // Update selected item types (checkboxes) when we clear/load a search
  useEffect(() => {
    setSelectedItemTypes(props.selectedItemTypes);
    return () => {};
  }, [setSelectedItemTypes, props.selectedItemTypes]);

  const [areaCoverageValues, setAreaCoverageValues] = useState(
    props.areaCoverage
  );
  const [filters, setFilters] = useState(props.filters);
  const [accessFilter, setAccessFilter] = useState(
    props.searchFilters.accessPermissions
  );
  const [satelliteId, setSatelliteId] = useState(
    props.searchFilters.satelliteId
  );
  const [instruments, setInstruments] = useState(
    props.searchFilters.instruments
  );
  const [publishingStages, setPublishingStages] = useState(
    props.searchFilters.publishingStages
  );

  useEffect(() => {
    setNewImageryCapabilities(props.selectedImageryCapabilities);
    setInstruments(props.searchFilters.instruments);
    setPublishingStages(props.searchFilters.publishingStages);
  }, [
    setNewImageryCapabilities,
    setInstruments,
    setPublishingStages,
    props.selectedImageryCapabilities,
    props.searchFilters,
  ]);

  const [localSR, setLocalSR] = useState(props.sr);

  const closePanel = () => {
    props.showFiltersPanel(false);
  };

  const onItemTypeChange = item => {
    const newState = {
      ...selectedItemTypes,
      ...item,
    };

    const truthyCount = Object.values(newState).filter(Boolean);
    // Don't allow the user to deselect the last item
    if (truthyCount.length === 0) {
      return;
    }

    setSelectedItemTypes(newState);
  };

  const updateFilters = newFilter => {
    const filtersCopy = {...filters};
    filtersCopy[newFilter.key] = {...newFilter};
    setFilters(filtersCopy);
  };

  const onFilterChangeComplete = filter => values => {
    updateFilters({...filter, values: values.slice()});
  };

  const onAreaCoverageChange = values => {
    setAreaCoverageValues(values);
  };

  const onFilterToggle = filter => event => {
    const {checked} = event.target;
    updateFilters({...filter, values: checked ? [filter.checked] : null});
  };

  const onAccessFilterChange = event => {
    setAccessFilter(!event.target.checked);
  };

  const onSatelliteIdFilterChange = event => {
    setSatelliteId(event.target.value);
  };

  const onInstrumentFilterChange = event => {
    const newState = {
      ...instruments,
      [event.target.name]: event.target.checked,
    };

    setInstruments(newState);
  };

  const onPublishingStageFilterChange = event => {
    const newState = {
      ...publishingStages,
      [event.target.name]: event.target.checked,
    };

    setPublishingStages(newState);
  };

  const onSubmitFilters = () => {
    // only submit truthy values
    // filter available item types. User may have loaded a saved search that
    // includes a deprecated item. We want to load it, but not show as available filter
    const newItemTypes = Object.keys(selectedItemTypes).filter(
      key => selectedItemTypes[key] && props.availableItemTypes.includes(key)
    );

    const newFilters = {};
    Object.values(filters).forEach(filter => {
      if (hasChangedFromDefaults(filter)) {
        trackEvent('Filter added', {
          filter: filter.key,
          filterValue: filter.values.toString(),
        });
      }
      newFilters[filter.key] = filter.values;
    });

    if (areaCoverageValues !== props.areaCoverage) {
      trackEvent('Filter added', {
        filter: 'area_coverage',
        filterValue: areaCoverageValues.toString(),
      });
    }

    trackEvent('Item Type filter submitted', {
      itemTypes: newItemTypes.toString(),
    });

    if (!isEqual(instruments, props.searchFilters.instruments)) {
      trackEvent('Filter added', {
        filter: 'instrument',
        instruments: instruments,
      });
    }

    if (!isEqual(publishingStages, props.searchFilters.publishingStages)) {
      trackEvent('Filter added', {
        filter: 'publishing_stage',
        stages: publishingStages,
      });
    }

    // only track if toggled on and previously off
    // we track here because we want to know at filter submission time
    // not when the user toggles without submitting
    if (localSR && localSR !== props.sr) {
      trackEvent('Filter added', {
        filter: 'sr',
        filterValue: localSR,
      });
    }

    // only if there are selections and have changed from previous
    if (!isEqual(newImageryCapabilities, props.selectedImageryCapabilities)) {
      trackEvent('Filter added', {
        filter: 'spectral_bands',
        filterValue: newImageryCapabilities,
      });
    }

    props.setSR(localSR);
    props.selectImageryCapabilities(newImageryCapabilities);
    props.selectItemTypes(newItemTypes);
    props.setAreaCoverage(areaCoverageValues);
    props.setSearchFilters({
      accessPermissions: accessFilter,
      satelliteId,
      instruments,
      publishingStages,
    });
    props.applyFilters(newFilters);
    closePanel();
  };

  const onClearAll = () => {
    const newFilters = {...filters};
    Object.values(newFilters).forEach(
      filter => (filter.values = filter.defaults)
    );

    setFilters(newFilters);
    setAreaCoverageValues(defaultAreaCoverage);
    setSelectedItemTypes(defaultItemTypes.selected);
    setAccessFilter(defaultSearchFilters.accessPermissions);
    setSatelliteId(defaultSearchFilters.satelliteId);
    setInstruments(defaultSearchFilters.instruments);
    setPublishingStages(defaultSearchFilters.publishingStages);
    setLocalSR(defaultSearchFilters.sr);
    setNewImageryCapabilities(defaultSearchFilters.selectedImageryCapabilities);
  };

  const renderToggleFilter = filter => {
    return (
      <ToggleFilter
        key={filter.key}
        title={t(filter.name)}
        description={t(filter.description)}
        checked={filter.values !== null}
        learnMore={filter.learnMore}
        onChange={onFilterToggle(filter)}
        data-qe={filter.name.replace(/\s+/g, '-').toLowerCase()}
      />
    );
  };

  const handleToggleImageryCapability = ({target: {name: capability}}) =>
    setNewImageryCapabilities(xor(newImageryCapabilities, [capability]));

  return (
    <React.Fragment>
      <LeftDrawerHeader
        title={t('filters.title')}
        onBackClick={closePanel}
        showBackButton
        backButtonProps={{'data-qe': 'filters-back-button'}}
        icon={
          <FilterAltOutlinedIcon
            sx={{color: theme => theme.palette.grey[500]}}
          />
        }
      />
      <Box height="100%" overflow="auto" flexGrow={1}>
        <FilterGroupPanel title={t('filters.sectionTitleType')}>
          <SourcesPicker
            selected={selectedItemTypes}
            onChange={onItemTypeChange}
          />
        </FilterGroupPanel>
        <Box>
          <FilterGroupPanel title={t('filters.sectionTitlePlanetscope')}>
            <SpectralBandsFilter
              selected={newImageryCapabilities}
              onChange={handleToggleImageryCapability}
            />
            <InstrumentFilter
              instruments={instruments}
              onChange={onInstrumentFilterChange}
            />
            <PublishingStageFilter
              stages={publishingStages}
              onChange={onPublishingStageFilterChange}
            />
          </FilterGroupPanel>
        </Box>
        <FilterGroupPanel
          title={t('filters.sectionTitleConditions')}
          dataQe="environmental-conditions-filter-group"
        >
          <SliderFilter
            name="filters.areaCoverageLabel"
            values={areaCoverageValues}
            unit="common:unit.percent"
            scale={x => Math.round(x * 100)}
            resetScale={x => x / 100}
            onChangeComplete={onAreaCoverageChange}
            dataQe="area-coverage"
          />
          {Object.values(filters).map(filter => {
            return (
              filter.type === 'RangeFilter' && (
                <SliderFilter
                  key={filter.key}
                  {...filter}
                  min={filter.range[0]}
                  max={filter.range[1]}
                  onChangeComplete={onFilterChangeComplete(filter)}
                  dataQe={filter.key}
                />
              )
            );
          })}
        </FilterGroupPanel>
        <FilterGroupPanel title={t('filters.sectionTitleAdvanced')}>
          <ToggleFilter
            title={t('filters.toggleShowAll')}
            description={t('filters.toggleShowAllDescription')}
            checked={!accessFilter}
            onChange={onAccessFilterChange}
            data-qe="imagery-access"
          />
          <ToggleFilter
            title={t('filters.toggleShowSr')}
            description={t('filters.toggleShowSrDescription')}
            checked={localSR}
            onChange={() => setLocalSR(!localSR)}
            data-qe="sr-filter"
          />

          {Object.values(filters).map(filter => {
            return filter.type !== 'RangeFilter' && renderToggleFilter(filter);
          })}
          {props.showSatelliteIdFilter && (
            <SatelliteIdFilter onChange={onSatelliteIdFilterChange} />
          )}
        </FilterGroupPanel>
      </Box>
      <AppBar position="static" elevation={0} color="transparent">
        <Divider />
        <CardActions style={{justifyContent: 'space-between'}}>
          <Button color="primary" fullWidth onClick={onClearAll}>
            {t('filters.buttonClearAll')}
          </Button>
          <Button
            variant="contained"
            color="primary"
            fullWidth
            onClick={onSubmitFilters}
            data-qe="apply-filters-button"
          >
            {t('filters.buttonApplyFilters')}
          </Button>
        </CardActions>
      </AppBar>
    </React.Fragment>
  );
};

FiltersPanel.propTypes = {
  applyFilters: PropTypes.func.isRequired,
  areaCoverage: PropTypes.arrayOf(PropTypes.number).isRequired,
  availableItemTypes: PropTypes.array,
  filters: PropTypes.object.isRequired,
  searchFilters: PropTypes.object,
  searchName: PropTypes.string,
  selectImageryCapabilities: PropTypes.func,
  selectItemTypes: PropTypes.func,
  selectedImageryCapabilities: PropTypes.array.isRequired,
  selectedItemTypes: PropTypes.object,
  setAreaCoverage: PropTypes.func.isRequired,
  setSR: PropTypes.func.isRequired,
  setSearchFilters: PropTypes.func,
  showFiltersPanel: PropTypes.func,
  showSatelliteIdFilter: PropTypes.bool.isRequired,
  sr: PropTypes.bool,
};

const mapStateToProps = state => ({
  areaCoverage: state.areaCoverage,
  availableItemTypes: getAvailableItemTypes(state).types,
  filters: getFiltersObj(state),
  searchFilters: state.searchFilters,
  searchName: state.search.name,
  selectedImageryCapabilities: getSelectedImageryCapabilities(state),
  selectedItemTypes: getSelectedItemTypes(state),
  showSatelliteIdFilter: !!state.labs[LABS_MAP.SATELLITE_ID_FILTER],
  sr: getSr(state),
});

const mapDispatchProps = {
  applyFilters,
  setAccessPermissions,
  setInstruments,
  setPublishingStages,
  setSatelliteId,
  selectItemTypes,
  setAreaCoverage,
  showFiltersPanel,
  resetSearchFilters,
  resetItemFilters,
  setSearchFilters,
  resetSelectedItemTypes,
  selectImageryCapabilities,
  setSR,
};

export default connect(mapStateToProps, mapDispatchProps)(FiltersPanel);
